import { type HTMLAttributes } from 'react'
import { cn } from '@/shared/lib/utils'

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Вариант skeleton
   * - `text`: для текстового контента (может быть несколько строк)
   * - `circular`: для круглых элементов (аватары, иконки)
   * - `rectangular`: для прямоугольных элементов (изображения, карточки)
   * @default 'rectangular'
   */
  variant?: 'text' | 'circular' | 'rectangular'
  /**
   * Количество строк (для text варианта)
   * Используется только когда variant="text"
   * @default 1
   */
  lines?: number
  /**
   * Ширина skeleton
   * Может быть строкой ('100%', '200px') или числом (пиксели)
   */
  width?: string | number
  /**
   * Высота skeleton
   * Может быть строкой ('100%', '200px') или числом (пиксели)
   */
  height?: string | number
}

/**
 * UI компонент: Skeleton loader
 * 
 * Компонент для отображения skeleton screens во время загрузки.
 * Использует pulse анимацию для улучшения воспринимаемой производительности.
 * 
 * @example
 * ```tsx
 * <Skeleton variant="text" lines={3} />
 * 
 * <Skeleton variant="circular" width={40} height={40} />
 * 
 * <Skeleton variant="rectangular" width="100%" height={200} />
 * ```
 */
export function Skeleton({
  variant = 'rectangular',
  lines = 1,
  width,
  height,
  className,
  ...props
}: SkeletonProps) {
  const baseStyles = cn(
    // Light theme - серо-графитовый с мягким градиентом
    'animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded',
    // Dark theme - менее яркий, серо-графитовый
    'dark:from-dark-elevated dark:via-dark-card dark:to-dark-elevated',
    {
      'rounded-full': variant === 'circular',
      'rounded-md': variant === 'text' || variant === 'rectangular',
    },
    className
  )

  if (variant === 'text' && lines > 1) {
    return (
      <div className="space-y-2" {...props}>
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={cn(
              baseStyles,
              'h-4',
              {
                'w-full': index === lines - 1,
                'w-3/4': index < lines - 1,
              }
            )}
            style={{
              width: index === lines - 1 ? width : undefined,
              height: height || undefined,
            }}
          />
        ))}
      </div>
    )
  }

  return (
    <div
      className={baseStyles}
      style={{
        width: width || (variant === 'circular' ? height : '100%'),
        height: height || (variant === 'text' ? '1rem' : undefined),
      }}
      {...props}
    />
  )
}

/**
 * Skeleton для карточки товара
 * 
 * Предзаготовленный skeleton компонент для отображения состояния загрузки
 * карточки товара. Включает skeleton для изображения, названия, категории, цены и кнопки.
 * 
 * @example
 * ```tsx
 * {isLoading ? <ProductCardSkeleton /> : <ProductCard product={product} />}
 * ```
 */
export function ProductCardSkeleton() {
  return (
    <div className="rounded-[16px] overflow-hidden bg-white shadow-md dark:bg-dark-card dark:shadow-md">
      {/* Изображение */}
      <Skeleton variant="rectangular" width="100%" height={200} className="rounded-none" />
      
      {/* Контент */}
      <div className="p-4 space-y-3">
        {/* Название */}
        <Skeleton variant="text" lines={2} />
        
        {/* Категория */}
        <Skeleton variant="text" width="40%" height={12} />
        
        {/* Цена */}
        <Skeleton variant="text" width="30%" height={20} />
        
        {/* Кнопка */}
        <Skeleton variant="rectangular" width="100%" height={44} />
      </div>
    </div>
  )
}

/**
 * Skeleton для списка товаров
 * 
 * Предзаготовленный skeleton компонент для отображения состояния загрузки
 * списка товаров. Отображает сетку из ProductCardSkeleton компонентов.
 * 
 * @param count - Количество skeleton карточек для отображения
 * @default 6
 * 
 * @example
 * ```tsx
 * {isLoading ? <ProductListSkeleton count={8} /> : <ProductGrid products={products} />}
 * ```
 */
export function ProductListSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {Array.from({ length: count }).map((_, index) => (
        <ProductCardSkeleton key={index} />
      ))}
    </div>
  )
}
